/**
 * MODULE: domain/sales-intelligence/frontend/hooks/useResearchWorkflow.ts
 * PURPOSE: React hook for managing company research workflow with SSE streaming
 * 
 * DEPENDENCIES:
 *   - react: useState, useCallback, useEffect
 *   - ../api/client: API calls
 * 
 * PUBLIC API:
 *   - useResearchWorkflow: Hook for research workflow with SSE
 * 
 * SIDE EFFECTS:
 *   - Triggers backend workflows
 *   - Establishes SSE connection for real-time updates
 * 
 * AGENT SAFETY: SAFE
 * FILE SIZE: 419 / 200 lines (exceeds limit - SSE streaming logic added)
 * 
 * Recent Changes:
 * - 2026-02-10: useResearchWorkflow(options?: { onComplete?: () => void }); onCompleteRef called after getInsight on 'complete' (named + onmessage) so parent can refetch; Research tab shows insights after Run Sales Intelligence.
 * - 2026-02-09 (Bug 3): Named 'thought' listener also appends to output — addEventListener('thought') now sets output: prev.output + line so streaming text appears when backend sends event: thought (onmessage does not run for named events).
 * - 2026-02-09: Thought events append to output — in 'thought' case, append content as "[type] content\n" to state.output so ResearchStatusPanel shows streaming text (replaces "Waiting for analysis...") with Initializing/Searching/Analyzing/Synthesizing messages.
 * - 2026-02-09: Modal results after complete — backend sends named SSE event "complete"; addEventListener('complete') calls getInsight(companyNameRef.current) and createActivity so modal shows results and View Company Profile.
 * - 2026-02-09: Preserve backend error when stream closes: onerror and addEventListener('error') use prev.error || message so specific backend error stays visible in modal (no overwrite by generic "Stream interrupted"). Error event handling supports object (streamEvent.data.error) or string payload; close eventSourceRef safely before nulling.
 * - 2026-02-08: Observability — receivedEventsRef; onerror shows specific message (backend unreachable vs stream interrupted); complete handler logs getInsight response in dev.
 * - 2026-02-07: Added thoughts and reasoning state tracking for LLM progress display; handle 'thought' and 'reasoning' SSE events (including addEventListener for named events)
 * - 2026-01-23: Fixed SSE connection error - use full backend URL (http://localhost:8080) instead of relative URL for EventSource compatibility
 * - 2026-01-23: Fixed uiComponents initialization - added uiComponents: [] to initial state to prevent undefined errors
 * - 2026-01-23: Added UI component state management - store uiComponents array in state and render LoadingSpinner, CompanyResearchCard, ErrorAlert components from SSE stream
 * - 2026-01-22: Updated to use orchestration-service endpoint `/api/v1/sales/research/stream` (GET)
 * - 2026-01-22: Added JWT token extraction from localStorage and parsing for tenant_id/user_id
 * - 2026-01-22: Updated event handlers to support orchestration service event types (ui_component, text_chunk)
 * - 2026-01-21: Added comprehensive debug logging for SSE connection and events
 * - 2026-01-21: Fixed URL parameters (removed research_query, backend only expects company_name)
 * 
 * See: /README.md#documentation | /ARCHITECTURE.md | /domain/sales-intelligence/README.md
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { getApiBaseUrl } from '@shared/lib/frontend/api/config';
import { getErrorMessage } from '@shared/lib/frontend/api/errors';
import { salesIntelligenceApi } from '../api/client';
import {
  ResearchRequest,
  CompanyInsight
} from '../../contracts/sales-intelligence.types';

// =====================================================
// Conditional Logging Helper
// =====================================================

const isDev = import.meta.env.DEV || import.meta.env.MODE === 'development';

const debugLog = (...args: unknown[]) => {
  if (isDev) {
    console.log(...args);
  }
};

// =====================================================
// Hook State Types (SSE stream from research workflow)
// =====================================================

/** Options for startResearch (e.g. dryRun); extend as backend supports more. */
export interface ResearchStartOptions {
  dryRun?: boolean;
  /** Research criteria toggles from SalesIntelligenceFormModal */
  criteria?: Record<string, boolean> | string[];
  customPrompt?: string;
  /**
   * When true with a subset `criteria`, orchestration saves only fields mapped to those sections
   * (user_overrides still win). Sent as partial_merge=1 on the SSE URL.
   */
  partialMerge?: boolean;
}

/** SSE event envelope: event type + payload (shape depends on event). */
interface ResearchStreamEvent {
  event: 'token' | 'text_chunk' | 'ui_component' | 'state_update' | 'tool_start' | 'tool_end' | 'thought' | 'reasoning' | 'complete' | 'error';
  data: Record<string, unknown> | string;
}

/** UI component from research stream (narrow when rendering). */
interface ResearchUIComponent {
  component: string;
  props: Record<string, unknown>;
  id?: string;
  message_id?: string;
  timestamp?: number;
}

/** Thought or reasoning item with optional type for ReasoningBadge */
export interface Thought {
  content: string;
  type?: 'system' | 'research' | 'analysis' | 'strategic' | 'technical' | 'competitive';
  timestamp?: string;
  progress?: number;
}

interface UseResearchWorkflowState {
  isLoading: boolean;
  insight: CompanyInsight | null;
  error: string | null;
  progress: number;
  currentStep: string | null;
  workflowId: string | null;
  output: string; // Accumulated streaming output
  uiComponents: ResearchUIComponent[]; // UI components from SSE stream
  thoughts: Thought[]; // LLM thought snippets with type badges
  reasoning: Thought[]; // Deep thinking / reasoning steps with type badges
}

interface UseResearchWorkflowReturn extends UseResearchWorkflowState {
  startResearch: (companyName: string, options?: ResearchStartOptions) => Promise<void>;
  reset: () => void;
}

// =====================================================
// Hook Implementation
// =====================================================

export function useResearchWorkflow(options?: { onComplete?: () => void }): UseResearchWorkflowReturn {
  const [state, setState] = useState<UseResearchWorkflowState>({
    isLoading: false,
    insight: null,
    error: null,
    progress: 0,
    currentStep: null,
    workflowId: null,
    output: '',
    uiComponents: [],
    thoughts: [],
    reasoning: []
  });
  
  const eventSourceRef = useRef<EventSource | null>(null);
  const receivedEventsRef = useRef<number>(0);
  /** Current company name for this run; used by named 'complete' listener (backend sends event: complete, so onmessage never runs). */
  const companyNameRef = useRef<string>('');
  /** Notify parent when research completes so it can refetch insight. */
  const onCompleteRef = useRef<(() => void) | null>(null);
  onCompleteRef.current = options?.onComplete ?? null;
  
  /**
   * Start company research with SSE streaming
   */
  const startResearch = useCallback(async (
    companyName: string,
    options?: ResearchStartOptions
  ) => {
    debugLog('🚀 startResearch called with:', companyName);
    companyNameRef.current = companyName;

    // Reset state
    setState({
      isLoading: true,
      insight: null,
      error: null,
      progress: 0,
      currentStep: 'Initializing...',
      workflowId: null,
      output: '',
      uiComponents: [],
      thoughts: [],
      reasoning: []
    });
    
    try {
      // Close existing connection if any
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      
      // Get auth token
      const token = localStorage.getItem('access_token');
      if (!token) {
        debugLog('⚠️ No token found in localStorage');
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: 'Authentication required. Please log in.'
        }));
        return;
      }

      // Parse token to get tenant_id and user_id (JWT payload is base64 encoded)
      let tenantId = 'default';
      let userId = 'default';
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        tenantId = payload.tenant_id || 'default';
        userId = payload.user_id || payload.sub || 'default';
        debugLog('✅ Extracted tenant_id:', tenantId, 'user_id:', userId);
      } catch (e) {
        debugLog('⚠️ Failed to parse token, using defaults:', e);
      }

      // Build URL with query params matching orchestration endpoint signature
      const params = new URLSearchParams({
        company_name: companyName,
        workspace_id: 'default', // TODO: Get from workspace context if available
        tenant_id: tenantId,
        user_id: userId
      });

      // Add optional criteria and custom prompt (from SalesIntelligenceFormModal)
      if (options?.criteria) {
        const list = Array.isArray(options.criteria)
          ? options.criteria
          : Object.entries(options.criteria)
              .filter(([, v]) => v)
              .map(([k]) => k);
        if (list.length > 0) {
          params.append('criteria', list.join(','));
        }
      }
      if (options?.customPrompt && options.customPrompt.trim()) {
        params.append('custom_prompt', options.customPrompt.trim());
      }
      if (options?.partialMerge === true) {
        params.append('partial_merge', 'true');
      }

      try {
        const enabledFields = await salesIntelligenceApi.getEnabledFields('company_research');
        if (enabledFields?.length) {
          params.append(
            'workflow_fields',
            JSON.stringify(
              enabledFields.map((f) => ({
                field_key: f.field_key,
                field_label: f.field_label,
                description: f.description,
                priority: f.priority,
                extraction_weight: f.extraction_weight,
              }))
            )
          );
        }
      } catch (wfErr) {
        debugLog('⚠️ Could not attach workflow_fields to research stream:', wfErr);
      }

      // Add token for authentication (orchestration middleware accepts token query param)
      params.append('token', token);
      debugLog('✅ Token added to request');

      // In dev use relative URL so Vite proxy forwards to API Gateway (8080) -> orchestration-service
      const baseURL = getApiBaseUrl();
      const url = `${baseURL}/api/v1/sales/research/stream?${params.toString()}`;
      
      // Mask token in logs for security
      debugLog('🌐 Creating EventSource with URL:', url.replace(/token=([^&]+)/, 'token=***'));
      
      // Create EventSource for SSE (only supports GET)
      const eventSource = new EventSource(url, {
        withCredentials: true
      });
      
      eventSourceRef.current = eventSource;
      
      eventSource.onopen = () => {
        debugLog('✅ EventSource connection opened');
      };
      
      // Track node progress (matches orchestration research graph node names)
      const nodeProgress: Record<string, number> = {
        'emit_loading': 5,
        'web_search': 25,
        'research': 60,
        'emit_results': 85,
        'save_to_database': 95,
        // SI company_research graph (node ids must differ from state keys; see company_research.py)
        'llm_step': 40,
        'fit_step': 55,
        'product_matching': 65,
        // Legacy/alternate names
        'llm_analysis': 40,
        'entity_extraction': 60,
        'insight_generation': 80,
        'storage': 100
      };

      // Human labels for state_update — must match company_research.py node ids (llm_step, fit_step, …)
      const NODE_LABELS: Record<string, string> = {
        web_search: 'Searching the web...',
        llm_step: 'Analysing with AI...',
        entity_extraction: 'Extracting entities...',
        fit_step: 'Scoring ICP fit...',
        product_matching: 'Matching products...',
        insight_generation: 'Generating insights...',
        storage: 'Saving results...',
        // Legacy graph ids (pre llm_step / fit_step rename)
        llm_analysis: 'Analysing with AI...',
        fit_analysis: 'Scoring ICP fit...',
      };
      
      // Reset received-events counter for this run
      receivedEventsRef.current = 0;

      // Handle SSE events
      // Note: Orchestration service uses different event types (ui_component, text_chunk, complete, error)
      eventSource.onmessage = (event: MessageEvent) => {
        receivedEventsRef.current += 1;
        debugLog('📨 Received SSE event:', event.data);
        try {
          const streamEvent = JSON.parse(event.data) as ResearchStreamEvent;
          
          switch (streamEvent.event) {
            case 'text_chunk':
            case 'token':
              // Accumulate streaming text chunks
              {
                const content =
                  typeof streamEvent.data === 'string'
                    ? streamEvent.data
                    : String(
                        streamEvent.data.content ??
                          streamEvent.data.text ??
                          ''
                      );
              setState(prev => ({
                ...prev,
                output: prev.output + content
              }));
              }
              break;
              
            case 'ui_component':
              // Handle UI component events from orchestration service
              debugLog('📦 UI component event:', streamEvent.data);
              // Store UI component in state for rendering
              if (streamEvent.data && typeof streamEvent.data === 'object') {
                setState(prev => ({
                  ...prev,
                  uiComponents: [
                    ...prev.uiComponents,
                    streamEvent.data as unknown as ResearchUIComponent,
                  ]
                }));
              }
              break;
              
            case 'state_update':
              // Update progress based on current node (backend emits node name + optional progress)
              {
              const d =
                typeof streamEvent.data === 'object' && streamEvent.data !== null
                  ? streamEvent.data
                  : {};
              const nodeStr =
                typeof d.node === 'string' ? d.node : d.node != null ? String(d.node) : '';
              const backendProgress = d.progress;
              const nodeProgressValue = typeof backendProgress === 'number'
                ? backendProgress
                : (nodeStr ? nodeProgress[nodeStr] : 0) || 0;
              const stepLabel = nodeStr
                ? (NODE_LABELS[nodeStr] ?? nodeStr.replace(/_/g, ' '))
                : null;

              setState(prev => ({
                ...prev,
                currentStep: stepLabel || prev.currentStep,
                progress: nodeProgressValue
              }));
              }
              break;
              
            case 'tool_start':
              {
              const td =
                typeof streamEvent.data === 'object' && streamEvent.data !== null
                  ? streamEvent.data
                  : {};
              const toolName = td.tool;
              debugLog('Tool invoked:', toolName);
              setState(prev => ({
                ...prev,
                currentStep: `Using ${String(toolName ?? 'tool')}...`
              }));
              }
              break;
              
            case 'tool_end':
              {
              const ted =
                typeof streamEvent.data === 'object' && streamEvent.data !== null
                  ? streamEvent.data
                  : {};
              debugLog('Tool completed:', ted.tool);
              }
              break;

            case 'thought':
              // LLM thought snippets with type for ReasoningBadge; may include progress. Also append to output so modal shows streaming text.
              {
                const th =
                  typeof streamEvent.data === 'object' && streamEvent.data !== null
                    ? streamEvent.data
                    : {};
                const thoughtContent =
                  typeof streamEvent.data === 'string'
                    ? streamEvent.data
                    : String(th.content ?? th.text ?? '');
                const thoughtProgress = th.progress;
                const thoughtType = (th.type as Thought['type']) || 'system';
                if (typeof thoughtContent === 'string' && thoughtContent.trim()) {
                  debugLog('💭 Thought event:', streamEvent.data);
                  const thought: Thought = {
                    content: thoughtContent.trim(),
                    type: thoughtType,
                    timestamp: th.timestamp as string | undefined,
                    progress: thoughtProgress as number | undefined
                  };
                  const line = `[${thoughtType}] ${thoughtContent.trim()}\n`;
                  setState(prev => ({
                    ...prev,
                    thoughts: [...prev.thoughts, thought],
                    output: prev.output + line,
                    ...(typeof thoughtProgress === 'number' && { progress: thoughtProgress })
                  }));
                }
              }
              break;

            case 'reasoning':
              // Deep thinking / reasoning steps with type for ReasoningBadge
              {
                const rh =
                  typeof streamEvent.data === 'object' && streamEvent.data !== null
                    ? streamEvent.data
                    : {};
                const reasoningContent =
                  typeof streamEvent.data === 'string'
                    ? streamEvent.data
                    : String(rh.content ?? rh.text ?? '');
                const reasoningType = (rh.type as Thought['type']) || 'analysis';
                if (typeof reasoningContent === 'string' && reasoningContent.trim()) {
                  debugLog('🧠 Reasoning event:', streamEvent.data);
                  const reasoningItem: Thought = {
                    content: reasoningContent.trim(),
                    type: reasoningType,
                    timestamp: rh.timestamp as string | undefined
                  };
                  setState(prev => ({
                    ...prev,
                    reasoning: [...prev.reasoning, reasoningItem]
                  }));
                }
              }
              break;
              
            case 'complete':
              {
              const cd =
                typeof streamEvent.data === 'object' && streamEvent.data !== null
                  ? streamEvent.data
                  : {};
              setState(prev => ({
                ...prev,
                isLoading: false,
                progress: 100,
                currentStep: 'Completed',
                workflowId: (cd.workflow_id as string | undefined) || prev.workflowId
              }));
              }
              
              // Fetch final insight (if available from sales-intelligence API)
              if (companyName) {
                salesIntelligenceApi.getInsight(companyName)
                  .then(async (response) => {
                    if (isDev) console.log('[PIPELINE:COMPLETE] getInsight response:', response);
                    const insight = response.insight;
                    setState(prev => ({
                      ...prev,
                      insight
                    }));
                    // Notify parent that research is complete so it can refetch
                    onCompleteRef.current?.();
                    // Save activity to company timeline for modal-triggered research
                    if (insight?.id) {
                      try {
                        await salesIntelligenceApi.createActivity(insight.id, {
                          activity_type: 'research',
                          notes: `AI research completed for ${companyName}`,
                          metadata: { source: 'modal' },
                        });
                      } catch (e) {
                        console.error('Failed to save activity:', e);
                      }
                    }
                  })
                  .catch(err => {
                    console.error('Failed to fetch insight:', err);
                    // Still notify parent even on fetch failure — data may be available
                    onCompleteRef.current?.();
                  });
              }
              
              // Close connection
              eventSource.close();
              eventSourceRef.current = null;
              break;
              
            case 'error':
              {
              const ed =
                typeof streamEvent.data === 'object' && streamEvent.data !== null
                  ? streamEvent.data
                  : null;
              const errorMessage =
                ed && typeof ed.error === 'string'
                  ? ed.error
                  : typeof streamEvent.data === 'string'
                    ? streamEvent.data
                    : 'Research failed';
              setState(prev => ({
                ...prev,
                isLoading: false,
                error: errorMessage,
                progress: 0
              }));
              if (eventSourceRef.current) {
                eventSourceRef.current.close();
                eventSourceRef.current = null;
              }
              }
              break;
          }
        } catch (error) {
          console.error('Error parsing SSE event:', error);
        }
      };
      
      // Handle event-specific listeners for orchestration service events
      eventSource.addEventListener('ui_component', (event: MessageEvent) => {
        debugLog('📦 UI component event:', event.data);
        try {
          const component = JSON.parse(event.data) as ResearchUIComponent;
          setState(prev => ({
            ...prev,
            uiComponents: [...prev.uiComponents, component]
          }));
        } catch (error) {
          console.error('Error parsing UI component:', error);
        }
      });
      
      eventSource.addEventListener('text_chunk', (event: MessageEvent) => {
        debugLog('📝 Text chunk event:', event.data);
        try {
          const chunk = JSON.parse(event.data);
          setState(prev => ({
            ...prev,
            output: prev.output + (chunk.content || '')
          }));
        } catch (error) {
          console.error('Error parsing text chunk:', error);
        }
      });

      // These addEventListener calls handle NAMED SSE events (event: thought / event: reasoning).
      // The switch-case in onmessage handles UNNAMED events (generic data: {...} messages).
      // They are NOT duplicates — they cover different SSE formats from the backend.
      eventSource.addEventListener('thought', (event: MessageEvent) => {
        debugLog('💭 Thought event:', event.data);
        try {
          const raw = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          const thoughtContent = raw?.content || raw?.text || '';
          if (thoughtContent) {
            const thoughtItem: Thought = {
              content: thoughtContent.trim(),
              type: raw?.type || 'system',
              timestamp: raw?.timestamp,
              progress: raw?.progress
            };
            const line = `[${thoughtItem.type}] ${thoughtItem.content}\n`;
            setState(prev => ({
              ...prev,
              thoughts: [...prev.thoughts, thoughtItem],
              output: prev.output + line,
              ...(typeof thoughtItem.progress === 'number' && { progress: thoughtItem.progress })
            }));
          }
        } catch (error) {
          console.error('Error parsing thought:', error);
        }
      });

      // Handle named 'reasoning' events (when backend uses event: reasoning in SSE)
      eventSource.addEventListener('reasoning', (event: MessageEvent) => {
        debugLog('🧠 Reasoning event:', event.data);
        try {
          const raw = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          const reasoningContent = raw?.content || raw?.text || '';
          if (reasoningContent) {
            const reasoningItem: Thought = {
              content: reasoningContent.trim(),
              type: raw?.type || 'analysis',
              timestamp: raw?.timestamp
            };
            setState(prev => ({
              ...prev,
              reasoning: [...prev.reasoning, reasoningItem]
            }));
          }
        } catch (error) {
          console.error('Error parsing reasoning:', error);
        }
      });
      
      eventSource.addEventListener('complete', (event: MessageEvent) => {
        debugLog('✅ Complete event:', event.data);
        const company = companyNameRef.current;
        setState(prev => ({
          ...prev,
          isLoading: false,
          progress: 100,
          currentStep: 'Completed'
        }));
        eventSource.close();
        eventSourceRef.current = null;
        // Backend sends event: "complete" (named SSE), so onmessage case 'complete' never runs.
        // Call getInsight here so insight is set and modal shows results / View Company Profile.
        if (company) {
          salesIntelligenceApi.getInsight(company)
            .then(async (response) => {
              if (isDev) console.log('[PIPELINE:COMPLETE] getInsight response:', response);
              const insight = response.insight;
              setState(prev => ({ ...prev, insight }));
              // Notify parent that research is complete so it can refetch
              onCompleteRef.current?.();
              if (insight?.id) {
                try {
                  await salesIntelligenceApi.createActivity(insight.id, {
                    activity_type: 'research',
                    notes: `AI research completed for ${company}`,
                    metadata: { source: 'modal' },
                  });
                } catch (e) {
                  console.error('Failed to save activity:', e);
                }
              }
            })
            .catch(err => {
              console.error('Failed to fetch insight after complete:', err);
              // Still notify parent even on fetch failure — data may be available
              onCompleteRef.current?.();
            });
        }
      });
      
      eventSource.addEventListener('error', (event: MessageEvent) => {
        debugLog('❌ Error event:', event.data);
        try {
          const errorData = JSON.parse(event.data);
          const message = errorData.error || 'Research failed';
          setState(prev => ({
            ...prev,
            isLoading: false,
            error: prev.error || message,
            progress: 0
          }));
        } catch (error) {
          setState(prev => ({
            ...prev,
            isLoading: false,
            error: prev.error || 'Research failed',
            progress: 0
          }));
        }
        eventSource.close();
        eventSourceRef.current = null;
      });
      
      eventSource.onerror = () => {
        const readyState = eventSource.readyState; // 0=CONNECTING, 1=OPEN, 2=CLOSED
        const received = receivedEventsRef.current;
        console.error('❌ SSE connection error. readyState:', readyState, '(0=CONNECTING, 1=OPEN, 2=CLOSED), receivedEvents:', received);
        console.error('EventSource URL:', eventSource.url);

        let errorMessage = 'Connection error. Please try again.';
        if (received === 0 && (readyState === 2 || readyState === 0)) {
          errorMessage = 'Backend unreachable. Check that orchestration service is running (e.g. make dev).';
        } else if (received > 0) {
          errorMessage = `Stream interrupted after receiving ${received} event(s). The research may have partially completed — check the Research tab.`;
        }

        setState(prev => ({
          ...prev,
          isLoading: false,
          // Keep backend error if already set (e.g. from 'error' event); don't overwrite with generic message when stream closes
          error: prev.error || errorMessage,
          progress: 0
        }));

        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
      };
      
    } catch (error: unknown) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: getErrorMessage(error, 'Failed to start research')
      }));
    }
  }, []);
  
  /**
   * Reset state
   */
  const reset = useCallback(() => {
    // Close SSE connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    
    setState({
      isLoading: false,
      insight: null,
      error: null,
      progress: 0,
      currentStep: null,
      workflowId: null,
      output: '',
      uiComponents: [],
      thoughts: [],
      reasoning: []
    });
  }, []);
  
  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);
  
  return {
    ...state,
    startResearch,
    reset
  };
}